import functionalFeaturesService from '@/services/embeds/functionalFeatures.service'
import codeEditorService from '@/services/embeds/plugin/code.editor.service'
import outputEditorService from '@/services/embeds/plugin/output.editor.service'
import wsService from '@/services/embeds/plugin/ws.service'
import recaptchaService from '@/services/recaptcha.service'
import { useAuthStore } from '@/stores/auth.store'
import { useEmbedConfigureStore } from '@/stores/embeds/embedConfigure.store'
import { useIdeStore } from '@/stores/embeds/ide.store'
import { SERVER_ERROR } from '@/utils/embeds/ide'

import axios from 'axios'
let controller: AbortController | null = null

export interface IExecuteRequest {
  script?: string | null
  language: string | null
  versionIndex: number
  projectKey: string | boolean | number
  multiFile?: boolean
  libs: string[]
  mainFile?: string
  args: string | null
  stdin: string | null
  hasInputFiles: boolean
  embedKey?: string | null
  isEmbed?: boolean | undefined
}
export interface IExecuteResponse {
  output?: string
  executionTime?: number
  memory?: number
  cpuTime?: number
  outputFiles?: string[]
}
/**
 * get libs based on language
 * @returns The libs
 */
const getLibs = () => {
  let selectedLanguagelibs: string[] = []
  useIdeStore().libsLanguage.forEach((language) => {
    if (Object.keys(language).includes(useIdeStore().selectedLanguage?.language as string)) {
      selectedLanguagelibs = language[useIdeStore().selectedLanguage?.language as string]
    }
  })
  return selectedLanguagelibs.length > 0 ? selectedLanguagelibs : []
}

/**
 * Execute the logic
 */
const executeLogic = async () => {
  let requestData: IExecuteRequest | null = null
  const url: string =
    useEmbedConfigureStore().getOnEditMode === false ? '/engine/execute-embed' : '/engine/execute'

  const language = useIdeStore().selectedLanguage?.language as string
  const versionIndex = useIdeStore().selectedVersion as number
  const script = codeEditorService.getScript()
  const args = useIdeStore().inputArgs
  const stdin = !useIdeStore().interActiveMode ? useIdeStore().stdinArgs : null
  const libs = getLibs()
  const projectKey =
    useEmbedConfigureStore().getOnEditMode === false
      ? (useIdeStore().selectedLanguage?.projectKey as string) || '1001'
      : 1001
  const hasInputFiles = useIdeStore().inputFiles.length > 0
  const embedKey =
    useEmbedConfigureStore().getOnEditMode === false
      ? useEmbedConfigureStore().getEmbed?.embedKey
      : null

  const isEmbed = useEmbedConfigureStore().getOnEditMode === false ? undefined : true

  // if (useIdeStore().isBlockly) {
  //   language = useIdeStore().isBlocklyLanguages[useIdeStore().versionIndex]
  //   versionIndex = useIdeStore().isBlocklyLanguageVersions[useIdeStore().versionIndex]
  // }
  // if (useIdeStore().isBlockly && language === 'php') {
  //   script = '\n' + script + '\n'
  // }

  requestData = {
    script,
    args,
    stdin,
    language,
    versionIndex,
    libs,
    projectKey,
    hasInputFiles,
    embedKey,
    isEmbed
  }

  if (useIdeStore().interActiveMode) {
    wsService.executeInteractive(requestData)
    return
  }

  await axios
    .post(url, JSON.stringify(requestData), {
      signal: controller?.signal,
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then((response: { data: IExecuteResponse }) => {
      if (response.data?.output) {
        outputEditorService.outputEditorSetProgram(response.data?.output)
        functionalFeaturesService.onExecuteCompleteEvent(response.data?.output as string)
      } else {
        outputEditorService.outputEditorSetProgram('')
      }
      outputEditorService.heightChangeFunction()

      useIdeStore().setExcecutionData(response.data)
    })
    .catch((error: { response: { status: number } }) => {
      if (error?.response?.status === 403) {
        useAuthStore().clearRobotCheck()
      }
      outputEditorService.outputEditorSetProgram(SERVER_ERROR)
      useIdeStore().resetExecutionMatrix()
    })
    .finally(() => {
      useIdeStore().setIsCodeExecuting(false)
    })
  useIdeStore().setIsCodeExecuting(false)
}
/**
 * Execute the code
 */
const execute = async () => {
  if (useIdeStore().interActiveMode) {
    useIdeStore().setStdinArgs('')
  }
  executeLogic()
}
/**
 * Try to execute the code
 */
const tryExecute = () => {
  if (useIdeStore().isCodeExecuting) return
  controller = new AbortController()

  useIdeStore().setIsCodeExecuting(true)
  useIdeStore().resetExecutionMatrix()
  useIdeStore().setOutputFiles([])

  outputEditorService.outputEditorSetProgram('JDoodle in Action.... Running the program...')
  recaptchaService
    .callViaCaptcha()
    .then(() => {
      execute()
    })
    .catch(() => {
      useIdeStore().setIsCodeExecuting(false)
      outputEditorService.outputEditorSetProgram(SERVER_ERROR)
      useIdeStore().resetExecutionMatrix()
    })
}
/**
 * stop execute the code
 */
const stopExecution = async () => {
  if (!useIdeStore().isCodeExecuting) return

  if (useIdeStore().interActiveMode) {
    wsService.stopExecuteInteractive()
  } else {
    controller?.abort()
    controller = null
  }

  useIdeStore().resetExecutionMatrix()
  useIdeStore().setIsCodeExecuting(false)
  await new Promise((resolve) => setTimeout(resolve, 100))
  outputEditorService.outputEditorSetProgram('Execution Stopped...')
}
export default { tryExecute, stopExecution }
